import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { mockTransactions, Transaction } from "@/lib/mockData";
import { cn } from "@/lib/utils";

const formatAmount = (amount: number, type: Transaction['type']) => {
  const absAmount = Math.abs(amount);
  return {
    value: `$${absAmount.toLocaleString()}`,
    isPositive: type === 'income'
  };
};

const getStatusBadge = (status: Transaction['status']) => {
  return status === 'cleared' 
    ? <Badge variant="outline" className="bg-success/10 text-success border-success/20">Cleared</Badge>
    : <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20">Pending</Badge>;
};

export function TransactionsTable() {
  return (
    <Card className="col-span-3 hover:shadow-lg transition-all duration-200 border-0 shadow-md">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <span>💳</span>
              <span>Recent Transactions</span>
            </CardTitle>
            <CardDescription>
              Your latest financial activity
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search transactions..."
              className="pl-10 bg-muted/50 border-0"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Category</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold text-right">Amount</TableHead>
                <TableHead className="font-semibold text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mockTransactions.slice(0, 8).map((transaction) => {
                const amount = formatAmount(transaction.amount, transaction.type);
                return (
                  <TableRow key={transaction.id} className="border-border hover:bg-muted/50">
                    <TableCell className="font-medium">
                      {new Date(transaction.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-accent/50">
                        {transaction.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {transaction.description}
                    </TableCell>
                    <TableCell className={cn(
                      "text-right font-semibold",
                      amount.isPositive ? "text-success" : "text-danger"
                    )}>
                      {amount.isPositive ? '+' : '-'}{amount.value}
                    </TableCell>
                    <TableCell className="text-center">
                      {getStatusBadge(transaction.status)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}